/**
 * Schedule Graph
 *
 * Interactive chart visualization of schedule values over time using Recharts.
 * Supports zoom, pan, and multiple schedule comparison.
 * Includes Year carpet plot view for annual visualization.
 */

import { useMemo, useState, useCallback } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import type { ParsedSchedule, SchedulePeriod, TimeValue, DayType, ScheduleTypeLimits } from './types';

/**
 * US Federal Holidays (fixed dates - actual holidays may vary)
 */
const US_HOLIDAYS: Array<{ month: number; day: number; name: string }> = [
  { month: 1, day: 1, name: "New Year's Day" },
  { month: 7, day: 4, name: 'Independence Day' },
  { month: 12, day: 25, name: 'Christmas Day' },
  // Note: MLK Day, Presidents Day, Memorial Day, Labor Day, Thanksgiving
  // are on varying dates - simplified to fixed dates for visualization
  { month: 1, day: 15, name: 'MLK Day (approx)' },
  { month: 2, day: 19, name: 'Presidents Day (approx)' },
  { month: 5, day: 27, name: 'Memorial Day (approx)' },
  { month: 9, day: 2, name: 'Labor Day (approx)' },
  { month: 11, day: 28, name: 'Thanksgiving (approx)' },
];

/**
 * Check if a date is a holiday
 */
function isHoliday(month: number, day: number): { isHoliday: boolean; name?: string } {
  const holiday = US_HOLIDAYS.find((h) => h.month === month && h.day === day);
  return holiday ? { isHoliday: true, name: holiday.name } : { isHoliday: false };
}

/**
 * Get day of week for a given day of year (0 = Monday, 6 = Sunday)
 * Assumes year starts on Monday for simplicity
 */
function getDayOfWeek(dayOfYear: number, startDayOfWeek = 0): number {
  return (dayOfYear - 1 + startDayOfWeek) % 7;
}

/**
 * Convert day of year to month/day
 */
function dayOfYearToDate(dayOfYear: number): { month: number; day: number } {
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  let remaining = dayOfYear;
  let month = 1;

  for (const days of daysInMonth) {
    if (remaining <= days) {
      return { month, day: remaining };
    }
    remaining -= days;
    month++;
  }

  return { month: 12, day: 31 };
}

/**
 * Check if a date is within a period
 */
function isDateInPeriod(month: number, day: number, period: SchedulePeriod): boolean {
  const dateNum = month * 100 + day;
  const startNum = period.startDate.month * 100 + period.startDate.day;
  const endNum = period.endDate.month * 100 + period.endDate.day;

  // Handle year wrap-around
  if (startNum <= endNum) {
    return dateNum >= startNum && dateNum <= endNum;
  } else {
    // Period wraps around year end
    return dateNum >= startNum || dateNum <= endNum;
  }
}

/**
 * Find the applicable period for a given date
 */
function findPeriodForDate(month: number, day: number, periods: SchedulePeriod[]): SchedulePeriod | undefined {
  for (const period of periods) {
    if (isDateInPeriod(month, day, period)) {
      return period;
    }
  }
  return periods[0]; // Fallback to first period
}

/**
 * Day type to day of week index (0 = Monday)
 */
function dayTypeToIndex(dayType: DayType): number[] {
  switch (dayType) {
    case 'Monday':
      return [0];
    case 'Tuesday':
      return [1];
    case 'Wednesday':
      return [2];
    case 'Thursday':
      return [3];
    case 'Friday':
      return [4];
    case 'Saturday':
      return [5];
    case 'Sunday':
      return [6];
    case 'Weekdays':
      return [0, 1, 2, 3, 4];
    case 'Weekends':
      return [5, 6];
    case 'AllDays':
      return [0, 1, 2, 3, 4, 5, 6];
    default:
      return [];
  }
}

/**
 * Generate hourly data points for a week
 */
function generateWeekData(
  period: SchedulePeriod | undefined,
  minValue: number,
  _maxValue: number
): Array<{ hour: number; day: number; value: number; label: string }> {
  const data: Array<{ hour: number; day: number; value: number; label: string }> = [];

  if (!period) {
    // Generate empty data
    for (let day = 0; day < 7; day++) {
      for (let hour = 0; hour < 24; hour++) {
        data.push({
          hour: day * 24 + hour,
          day,
          value: minValue,
          label: `Day ${String(day + 1)}, ${String(hour).padStart(2, '0')}:00`,
        });
      }
    }
    return data;
  }

  // Build a map of day index -> time values
  const dayValues = new Map<number, TimeValue[]>();

  for (const ds of period.daySchedules) {
    for (const dt of ds.dayTypes) {
      const indices = dayTypeToIndex(dt);
      for (const idx of indices) {
        if (!dayValues.has(idx)) {
          dayValues.set(idx, ds.values);
        }
      }
    }
  }

  // Check for AllOtherDays fallback
  const allOtherDs = period.daySchedules.find((ds) => ds.dayTypes.includes('AllOtherDays'));

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  for (let day = 0; day < 7; day++) {
    const values = dayValues.get(day) || allOtherDs?.values || [];

    // Generate hourly values
    let currentValue = minValue;
    let valueIndex = 0;

    // Sort values by time
    const sortedValues = [...values].sort((a, b) => {
      const aMinutes = a.hour * 60 + a.minute;
      const bMinutes = b.hour * 60 + b.minute;
      return aMinutes - bMinutes;
    });

    for (let hour = 0; hour < 24; hour++) {
      // Find the value that applies to this hour
      while (valueIndex < sortedValues.length) {
        const tv = sortedValues[valueIndex];
        if (!tv) break;
        const tvHour = tv.hour === 24 ? 24 : tv.hour;
        const tvMinutes = tvHour * 60 + tv.minute;
        const hourEnd = (hour + 1) * 60;

        if (tvMinutes <= hourEnd) {
          currentValue = tv.value;
          if (tvMinutes <= hour * 60) {
            valueIndex++;
          } else {
            break;
          }
        } else {
          break;
        }
      }

      data.push({
        hour: day * 24 + hour,
        day,
        value: currentValue,
        label: `${dayNames[day]}, ${String(hour).padStart(2, '0')}:00`,
      });
    }
  }

  return data;
}

/**
 * Data point for year carpet plot
 */
interface YearDataPoint {
  dayOfYear: number;
  hour: number;
  value: number;
  date: { month: number; day: number };
  dayOfWeek: number;
  dayType: string;
  isHoliday: boolean;
  holidayName: string | undefined;
  periodId: string | undefined;
}

/**
 * Generate year-long hourly data for carpet plot
 */
function generateYearData(
  schedule: ParsedSchedule,
  minValue: number
): { data: YearDataPoint[][]; designDays: { summer: TimeValue[]; winter: TimeValue[] } } {
  const DAYS_IN_YEAR = 365;
  const data: YearDataPoint[][] = [];

  // Extract design day schedules from all periods
  let summerDesignDay: TimeValue[] = [];
  let winterDesignDay: TimeValue[] = [];

  for (const period of schedule.periods) {
    for (const ds of period.daySchedules) {
      if (ds.dayTypes.includes('SummerDesignDay') && summerDesignDay.length === 0) {
        summerDesignDay = ds.values;
      }
      if (ds.dayTypes.includes('WinterDesignDay') && winterDesignDay.length === 0) {
        winterDesignDay = ds.values;
      }
    }
  }

  // Generate data for each day of year
  for (let dayOfYear = 1; dayOfYear <= DAYS_IN_YEAR; dayOfYear++) {
    const dayData: YearDataPoint[] = [];
    const { month, day } = dayOfYearToDate(dayOfYear);
    const dayOfWeek = getDayOfWeek(dayOfYear);
    const holidayCheck = isHoliday(month, day);

    // Find applicable period
    const period = findPeriodForDate(month, day, schedule.periods);

    // Determine day type for this date
    let applicableDayType = 'Unknown';
    let values: TimeValue[] = [];

    if (period) {
      // Check for holiday schedule first
      if (holidayCheck.isHoliday) {
        const holidaySchedule = period.daySchedules.find((ds) => ds.dayTypes.includes('Holidays'));
        if (holidaySchedule) {
          applicableDayType = 'Holidays';
          values = holidaySchedule.values;
        }
      }

      // If no holiday schedule or not a holiday, find by day of week
      if (values.length === 0) {
        const dayTypeNames: DayType[] = [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
          'Sunday',
        ];
        const dayTypeName = dayTypeNames[dayOfWeek];
        applicableDayType = dayTypeName ?? 'Unknown';

        // Try specific day first
        let ds = period.daySchedules.find((d) => d.dayTypes.includes(dayTypeName ?? 'Monday'));

        // Try Weekdays/Weekends
        if (!ds && dayOfWeek < 5) {
          ds = period.daySchedules.find((d) => d.dayTypes.includes('Weekdays'));
        }
        if (!ds && dayOfWeek >= 5) {
          ds = period.daySchedules.find((d) => d.dayTypes.includes('Weekends'));
        }

        // Try AllDays
        if (!ds) {
          ds = period.daySchedules.find((d) => d.dayTypes.includes('AllDays'));
        }

        // Try AllOtherDays as fallback
        if (!ds) {
          ds = period.daySchedules.find((d) => d.dayTypes.includes('AllOtherDays'));
        }

        if (ds) {
          values = ds.values;
          applicableDayType = ds.dayTypes[0] ?? applicableDayType;
        }
      }
    } else {
      applicableDayType = 'Unknown';
    }

    // Generate hourly values
    const sortedValues = [...values].sort((a, b) => {
      const aMinutes = a.hour * 60 + a.minute;
      const bMinutes = b.hour * 60 + b.minute;
      return aMinutes - bMinutes;
    });

    let currentValue = minValue;
    let valueIndex = 0;

    for (let hour = 0; hour < 24; hour++) {
      // Find the value that applies to this hour
      while (valueIndex < sortedValues.length) {
        const tv = sortedValues[valueIndex];
        if (!tv) break;
        const tvHour = tv.hour === 24 ? 24 : tv.hour;
        const tvMinutes = tvHour * 60 + tv.minute;
        const hourEnd = (hour + 1) * 60;

        if (tvMinutes <= hourEnd) {
          currentValue = tv.value;
          if (tvMinutes <= hour * 60) {
            valueIndex++;
          } else {
            break;
          }
        } else {
          break;
        }
      }

      dayData.push({
        dayOfYear,
        hour,
        value: currentValue,
        date: { month, day },
        dayOfWeek,
        dayType: applicableDayType,
        isHoliday: holidayCheck.isHoliday,
        holidayName: holidayCheck.name,
        periodId: period?.id,
      });
    }

    data.push(dayData);
  }

  return {
    data,
    designDays: {
      summer: summerDesignDay,
      winter: winterDesignDay,
    },
  };
}

/**
 * Color scale for carpet plot - matches the DayEditor bar colors
 * Uses the same color gradient as the edit panel for visual consistency:
 * - blue-300 (low) -> blue-400 -> amber-400 -> orange-500 (high)
 *
 * Tailwind color values:
 * - blue-300: rgb(147, 197, 253)
 * - blue-400: rgb(96, 165, 250)
 * - amber-400: rgb(251, 191, 36)
 * - orange-500: rgb(249, 115, 22)
 */
function getHeatmapColor(value: number, minValue: number, maxValue: number): string {
  const range = maxValue - minValue;
  if (range === 0) return 'rgb(147, 197, 253)'; // blue-300 when no range

  const normalized = (value - minValue) / range;

  // Match DayEditor thresholds: <= 0.1 blue-300, 0.1-0.4 blue-400, 0.4-0.7 amber-400, > 0.7 orange-500
  if (normalized <= 0.1) {
    // blue-300 (solid for lowest values)
    return 'rgb(147, 197, 253)';
  } else if (normalized <= 0.4) {
    // blue-300 to blue-400 transition
    const t = (normalized - 0.1) / 0.3;
    return `rgb(${Math.round(147 - t * 51)}, ${Math.round(197 - t * 32)}, ${Math.round(253 - t * 3)})`;
  } else if (normalized <= 0.7) {
    // blue-400 to amber-400 transition
    const t = (normalized - 0.4) / 0.3;
    return `rgb(${Math.round(96 + t * 155)}, ${Math.round(165 + t * 26)}, ${Math.round(250 - t * 214)})`;
  } else {
    // amber-400 to orange-500 transition
    const t = (normalized - 0.7) / 0.3;
    return `rgb(${Math.round(251 - t * 2)}, ${Math.round(191 - t * 76)}, ${Math.round(36 - t * 14)})`;
  }
}

/**
 * Month labels and their starting day positions
 */
const MONTH_STARTS = [
  { name: 'Jan', day: 1 },
  { name: 'Feb', day: 32 },
  { name: 'Mar', day: 60 },
  { name: 'Apr', day: 91 },
  { name: 'May', day: 121 },
  { name: 'Jun', day: 152 },
  { name: 'Jul', day: 182 },
  { name: 'Aug', day: 213 },
  { name: 'Sep', day: 244 },
  { name: 'Oct', day: 274 },
  { name: 'Nov', day: 305 },
  { name: 'Dec', day: 335 },
];

/**
 * Year Carpet Plot Component
 */
function YearCarpetPlot({
  schedule,
  typeLimits,
  height = 300,
}: {
  schedule: ParsedSchedule;
  typeLimits?: ScheduleTypeLimits | null;
  height?: number;
}) {
  const [hoveredCell, setHoveredCell] = useState<YearDataPoint | null>(null);
  const [showDesignDays, setShowDesignDays] = useState(false);

  const minValue = typeLimits?.lowerLimit ?? 0;
  const maxValue = typeLimits?.upperLimit ?? 1;

  // Generate year data
  const { data: yearData, designDays } = useMemo(() => {
    return generateYearData(schedule, minValue);
  }, [schedule, minValue]);

  // Calculate actual min/max from data
  const { actualMin, actualMax } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const dayData of yearData) {
      for (const point of dayData) {
        min = Math.min(min, point.value);
        max = Math.max(max, point.value);
      }
    }
    return { actualMin: min === Infinity ? minValue : min, actualMax: max === -Infinity ? maxValue : max };
  }, [yearData, minValue, maxValue]);

  // Use type limits if available, otherwise use actual data range
  const colorMin = typeLimits?.lowerLimit ?? actualMin;
  const colorMax = typeLimits?.upperLimit ?? actualMax;

  // Calculate cell dimensions
  const padding = { top: 30, right: 60, bottom: 40, left: 50 };
  const plotWidth = 800; // Will be scaled by container
  const plotHeight = height - padding.top - padding.bottom;
  const cellWidth = (plotWidth - padding.left - padding.right) / 365;
  const cellHeight = plotHeight / 24;

  // Generate design day hourly values
  const getDesignDayHourlyValues = useCallback(
    (values: TimeValue[]): number[] => {
      const sortedValues = [...values].sort((a, b) => {
        const aMinutes = a.hour * 60 + a.minute;
        const bMinutes = b.hour * 60 + b.minute;
        return aMinutes - bMinutes;
      });

      const hourlyValues: number[] = [];
      let currentValue = minValue;
      let valueIndex = 0;

      for (let hour = 0; hour < 24; hour++) {
        while (valueIndex < sortedValues.length) {
          const tv = sortedValues[valueIndex];
          if (!tv) break;
          const tvHour = tv.hour === 24 ? 24 : tv.hour;
          const tvMinutes = tvHour * 60 + tv.minute;
          const hourEnd = (hour + 1) * 60;

          if (tvMinutes <= hourEnd) {
            currentValue = tv.value;
            if (tvMinutes <= hour * 60) {
              valueIndex++;
            } else {
              break;
            }
          } else {
            break;
          }
        }
        hourlyValues.push(currentValue);
      }

      return hourlyValues;
    },
    [minValue]
  );

  const hasDesignDays = designDays.summer.length > 0 || designDays.winter.length > 0;

  return (
    <div className="relative">
      {/* Design days toggle */}
      {hasDesignDays && (
        <div className="absolute right-2 top-2 z-10">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={showDesignDays}
              onChange={(e) => setShowDesignDays(e.target.checked)}
              className="h-3 w-3 rounded border-border"
            />
            Show Design Days
          </label>
        </div>
      )}

      {/* Main carpet plot */}
      <svg
        viewBox={`0 0 ${plotWidth} ${height}`}
        className="w-full"
        style={{ maxHeight: height }}
      >
        {/* Background */}
        <rect x={padding.left} y={padding.top} width={plotWidth - padding.left - padding.right} height={plotHeight} fill="#f8fafc" />

        {/* Month grid lines and labels */}
        {MONTH_STARTS.map((month) => (
          <g key={month.name}>
            <line
              x1={padding.left + (month.day - 1) * cellWidth}
              y1={padding.top}
              x2={padding.left + (month.day - 1) * cellWidth}
              y2={padding.top + plotHeight}
              stroke="#e5e7eb"
              strokeWidth={0.5}
            />
            <text
              x={padding.left + (month.day - 1 + 15) * cellWidth}
              y={height - 10}
              textAnchor="middle"
              className="fill-muted-foreground"
              style={{ fontSize: '10px' }}
            >
              {month.name}
            </text>
          </g>
        ))}

        {/* Hour labels */}
        {[0, 6, 12, 18, 24].map((hour) => (
          <g key={hour}>
            {hour < 24 && (
              <line
                x1={padding.left}
                y1={padding.top + hour * cellHeight}
                x2={plotWidth - padding.right}
                y2={padding.top + hour * cellHeight}
                stroke="#e5e7eb"
                strokeWidth={0.5}
              />
            )}
            <text
              x={padding.left - 8}
              y={padding.top + hour * cellHeight + 4}
              textAnchor="end"
              className="fill-muted-foreground"
              style={{ fontSize: '10px' }}
            >
              {String(hour).padStart(2, '0')}:00
            </text>
          </g>
        ))}

        {/* Data cells */}
        {yearData.map((dayData, dayIndex) =>
          dayData.map((point) => (
            <rect
              key={`${dayIndex}-${point.hour}`}
              x={padding.left + dayIndex * cellWidth}
              y={padding.top + point.hour * cellHeight}
              width={cellWidth + 0.5}
              height={cellHeight + 0.5}
              fill={getHeatmapColor(point.value, colorMin, colorMax)}
              onMouseEnter={() => setHoveredCell(point)}
              onMouseLeave={() => setHoveredCell(null)}
              style={{ cursor: 'crosshair' }}
            />
          ))
        )}

        {/* Holiday markers */}
        {yearData.map((dayData, dayIndex) => {
          const point = dayData[0];
          if (point?.isHoliday) {
            return (
              <line
                key={`holiday-${dayIndex}`}
                x1={padding.left + dayIndex * cellWidth + cellWidth / 2}
                y1={padding.top - 3}
                x2={padding.left + dayIndex * cellWidth + cellWidth / 2}
                y2={padding.top - 8}
                stroke="#ef4444"
                strokeWidth={2}
              />
            );
          }
          return null;
        })}

        {/* Axis labels */}
        <text
          x={plotWidth / 2}
          y={height - 2}
          textAnchor="middle"
          className="fill-muted-foreground"
          style={{ fontSize: '11px' }}
        >
          Day of Year
        </text>
        <text
          x={12}
          y={padding.top + plotHeight / 2}
          textAnchor="middle"
          className="fill-muted-foreground"
          style={{ fontSize: '11px' }}
          transform={`rotate(-90, 12, ${padding.top + plotHeight / 2})`}
        >
          Hour of Day
        </text>

        {/* Color scale legend */}
        <defs>
          <linearGradient id="colorScale" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor={getHeatmapColor(colorMin, colorMin, colorMax)} />
            <stop offset="25%" stopColor={getHeatmapColor(colorMin + (colorMax - colorMin) * 0.25, colorMin, colorMax)} />
            <stop offset="50%" stopColor={getHeatmapColor(colorMin + (colorMax - colorMin) * 0.5, colorMin, colorMax)} />
            <stop offset="75%" stopColor={getHeatmapColor(colorMin + (colorMax - colorMin) * 0.75, colorMin, colorMax)} />
            <stop offset="100%" stopColor={getHeatmapColor(colorMax, colorMin, colorMax)} />
          </linearGradient>
        </defs>
        <rect
          x={plotWidth - padding.right + 15}
          y={padding.top}
          width={15}
          height={plotHeight}
          fill="url(#colorScale)"
          stroke="#d1d5db"
          strokeWidth={0.5}
        />
        <text
          x={plotWidth - padding.right + 35}
          y={padding.top + 4}
          className="fill-muted-foreground"
          style={{ fontSize: '9px' }}
        >
          {colorMax.toFixed(1)}
        </text>
        <text
          x={plotWidth - padding.right + 35}
          y={padding.top + plotHeight}
          className="fill-muted-foreground"
          style={{ fontSize: '9px' }}
        >
          {colorMin.toFixed(1)}
        </text>
      </svg>

      {/* Design days panel */}
      {showDesignDays && hasDesignDays && (
        <div className="mt-2 grid grid-cols-2 gap-2 border-t border-border pt-2">
          {designDays.summer.length > 0 && (
            <div className="rounded border border-orange-300 bg-orange-50 p-2 dark:border-orange-700 dark:bg-orange-950/30">
              <div className="mb-1 flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-orange-500" />
                <span className="text-xs font-medium text-orange-700 dark:text-orange-400">Summer Design Day</span>
              </div>
              <div className="flex h-6 items-end gap-px">
                {getDesignDayHourlyValues(designDays.summer).map((value, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-t"
                    style={{
                      height: `${((value - colorMin) / (colorMax - colorMin)) * 100}%`,
                      minHeight: value > colorMin ? '2px' : '0',
                      backgroundColor: getHeatmapColor(value, colorMin, colorMax),
                    }}
                  />
                ))}
              </div>
            </div>
          )}
          {designDays.winter.length > 0 && (
            <div className="rounded border border-blue-300 bg-blue-50 p-2 dark:border-blue-700 dark:bg-blue-950/30">
              <div className="mb-1 flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-blue-500" />
                <span className="text-xs font-medium text-blue-700 dark:text-blue-400">Winter Design Day</span>
              </div>
              <div className="flex h-6 items-end gap-px">
                {getDesignDayHourlyValues(designDays.winter).map((value, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-t"
                    style={{
                      height: `${((value - colorMin) / (colorMax - colorMin)) * 100}%`,
                      minHeight: value > colorMin ? '2px' : '0',
                      backgroundColor: getHeatmapColor(value, colorMin, colorMax),
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tooltip */}
      {hoveredCell && (
        <div className="pointer-events-none absolute left-1/2 top-4 z-20 -translate-x-1/2 rounded-lg border border-border bg-popover px-3 py-2 shadow-lg">
          <div className="text-xs">
            <p className="font-semibold text-popover-foreground">
              {hoveredCell.date.month}/{hoveredCell.date.day} at {String(hoveredCell.hour).padStart(2, '0')}:00
            </p>
            <p className="text-muted-foreground">
              Value: <span className="font-medium text-primary">{hoveredCell.value.toFixed(3)}</span>
            </p>
            <p className="text-muted-foreground">
              {hoveredCell.dayType}
              {hoveredCell.isHoliday && (
                <span className="ml-1 text-red-500">({hoveredCell.holidayName})</span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Legend for markers */}
      <div className="mt-1 flex items-center justify-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <div className="h-2 w-2 rounded-full bg-red-500" />
          <span>Holiday</span>
        </div>
        {hasDesignDays && (
          <div className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-orange-500" />
            <span>Design days available</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Custom tooltip component
 */
function CustomTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: { label: string; value: number } }>;
}) {
  if (active && payload?.[0]) {
    const data = payload[0].payload;
    return (
      <div className="rounded-lg border border-border bg-popover px-3 py-2 shadow-lg">
        <p className="text-xs font-medium text-muted-foreground">{data.label}</p>
        <p className="text-sm font-semibold text-primary">{data.value.toFixed(2)}</p>
      </div>
    );
  }
  return null;
}

interface ScheduleGraphProps {
  /** Schedule to visualize */
  schedule: ParsedSchedule | null;
  /** Selected period ID */
  selectedPeriodId: string | null;
  /** Schedule type limits for axis bounds */
  typeLimits?: ScheduleTypeLimits | null;
  /** Height of the chart */
  height?: number;
}

/**
 * Schedule Graph Component
 */
export function ScheduleGraph({
  schedule,
  selectedPeriodId,
  typeLimits,
  height = 200,
}: ScheduleGraphProps) {
  const [viewMode, setViewMode] = useState<'week' | 'day' | 'year'>('week');
  const [selectedDay, setSelectedDay] = useState(0);

  const minValue = typeLimits?.lowerLimit ?? 0;
  const maxValue = typeLimits?.upperLimit ?? 1;

  // Get selected period
  const selectedPeriod = useMemo(() => {
    if (!schedule) return undefined;
    if (selectedPeriodId) {
      return schedule.periods.find((p) => p.id === selectedPeriodId);
    }
    return schedule.periods[0];
  }, [schedule, selectedPeriodId]);

  // Generate chart data
  const weekData = useMemo(() => {
    return generateWeekData(selectedPeriod, minValue, maxValue);
  }, [selectedPeriod, minValue, maxValue]);

  // Filter for day view
  const displayData = useMemo(() => {
    if (viewMode === 'day') {
      return weekData.filter((d) => d.day === selectedDay);
    }
    return weekData;
  }, [weekData, viewMode, selectedDay]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (displayData.length === 0) return { min: 0, max: 0, avg: 0, peak: 0 };

    const values = displayData.map((d) => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;

    // Find peak hours
    let peakHours = 0;
    const threshold = max * 0.8;
    for (const v of values) {
      if (v >= threshold) peakHours++;
    }

    return { min, max, avg, peakHours };
  }, [displayData]);

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  if (!schedule) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-center">
        <svg
          className="mx-auto h-12 w-12 text-muted-foreground/50"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M3 3v18h18M7 16l4-4 4 4 5-6" />
        </svg>
        <p className="mt-2 text-sm text-muted-foreground">Select a schedule to view its graph</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <div>
          <h3 className="text-sm font-medium text-foreground">Schedule Preview</h3>
          <p className="text-xs text-muted-foreground">{schedule.name}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          <div className="flex rounded-md border border-border">
            <button
              onClick={() => setViewMode('day')}
              className={`px-2 py-1 text-xs ${
                viewMode === 'day'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card text-muted-foreground hover:bg-muted'
              } rounded-l-md`}
            >
              Day
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-2 py-1 text-xs ${
                viewMode === 'week'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card text-muted-foreground hover:bg-muted'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setViewMode('year')}
              className={`px-2 py-1 text-xs ${
                viewMode === 'year'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card text-muted-foreground hover:bg-muted'
              } rounded-r-md`}
            >
              Year
            </button>
          </div>

          {/* Day selector (when in day view) */}
          {viewMode === 'day' && (
            <select
              value={selectedDay}
              onChange={(e) => setSelectedDay(parseInt(e.target.value, 10))}
              className="rounded-md border border-border bg-card px-2 py-1 text-xs text-foreground"
            >
              {dayNames.map((name, i) => (
                <option key={i} value={i}>
                  {name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="p-4">
        {viewMode === 'year' ? (
          <YearCarpetPlot schedule={schedule} typeLimits={typeLimits ?? null} height={Math.max(height, 300)} />
        ) : (
          <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={displayData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="hour"
                tickFormatter={(value: number) => {
                  if (viewMode === 'week') {
                    const day = Math.floor(value / 24);
                    const hour = value % 24;
                    if (hour === 0) return dayNames[day] || '';
                    if (hour === 12) return '12';
                    return '';
                  }
                  return `${String(value % 24).padStart(2, '0')}:00`;
                }}
                tick={{ fontSize: 10 }}
                stroke="#9CA3AF"
                interval={viewMode === 'week' ? 11 : 3}
              />
              <YAxis
                domain={[minValue, maxValue]}
                tick={{ fontSize: 10 }}
                stroke="#9CA3AF"
                width={40}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={maxValue * 0.5} stroke="#E5E7EB" strokeDasharray="3 3" />
              <Area
                type="stepAfter"
                dataKey="value"
                stroke="#3B82F6"
                fill="url(#colorValue)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Statistics (not shown for year view) */}
      {viewMode !== 'year' && (
        <div className="border-t border-border px-4 py-2">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-xs text-muted-foreground">Min</p>
              <p className="text-sm font-medium text-foreground">{stats.min.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Max</p>
              <p className="text-sm font-medium text-foreground">{stats.max.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Average</p>
              <p className="text-sm font-medium text-foreground">{stats.avg.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Peak Hours</p>
              <p className="text-sm font-medium text-foreground">{stats.peakHours}h</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
